import React from 'react'
import {
  PageHeading,
  Heading,
  List,
  Code,
  Markdown,
} from '../shared/typography'
import { Link as RouterLink } from 'react-router-dom'
import dig from 'object-dig'
import request from '../../api/request'
import Loading from '../shared/loading'
import ErrorUI from '../shared/error_ui'
import Table from '../shared/table'
import { BetaBadge } from '../shared/badges'
import { StackView, Badge, Box } from '@planningcenter/tapestry-react'

const Details = ({ id, details, beta }) => {
  return (
    <Box>
      <StackView axis="horizontal" alignment="center" marginBottom={1}>
        <PageHeading size={1}>{id}</PageHeading>
        {beta && <BetaBadge marginLeft={1} />}
      </StackView>
      <Markdown>{details}</Markdown>
    </Box>
  )
}

const ChangeTypeBadge = ({ type }) => {
  const color = (function (type) {
    switch (type) {
      case 'breaking':
        return 'error'
      case 'modified':
        return 'blue-4'
      default:
        throw new Error(`Unknown change type: ${type}`)
    }
  })(type)

  return (
    <Badge
      title={type}
      size="sm"
      color={{ foreground: '#fff', background: color }}
      radius={4}
      style={{
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        padding: '4px 6px',
      }}
    />
  )
}

const Changes = ({ changes }) => {
  if (changes.length === 0) return null

  const columns = [
    {
      header: 'Type',
      cell: row => <ChangeTypeBadge type={row.type} />,
    },
    {
      header: 'Changes to',
      cell: row => <Code>{row.id}</Code>,
    },
    {
      header: 'Notes',
      cell: row => <Markdown>{row.message}</Markdown>,
    },
  ]

  const data = changes.flatMap(change =>
    change.relationships.changes.data.map(obj => ({
      id: change.id,
      type: obj.attributes.type,
      message: obj.attributes.message,
    }))
  )

  return (
    <StackView axis="vertical" spacing={1}>
      <Heading>Changes</Heading>
      <Table columns={columns} data={data} />
    </StackView>
  )
}

const Removed = ({ removed, appName }) => {
  if (removed.length === 0) {
    return null
  }
  return (
    <StackView axis="vertical" spacing={1}>
      <Heading>Removed</Heading>
      <List>
        {removed.map(rem => {
          return (
            <List.Item key={rem.id}>
              <Code>{rem.attributes.name}</Code>{' '}
              <span>
                (Deprecated in{' '}
                <RouterLink
                  to={`/apps/${appName}/${rem.attributes.deprecated_in}`}
                >
                  {rem.attributes.deprecated_in}
                </RouterLink>
                )
              </span>
            </List.Item>
          )
        })}
      </List>
    </StackView>
  )
}

export default class Version extends React.Component {
  state = {
    error: null,
    isLoading: true,
    versionData: {},
  }

  componentDidMount() {
    this.fetchData()
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (
      props.match.params.appName !== this.props.match.params.appName ||
      props.match.params.version !== this.props.match.params.version
    ) {
      this.fetchData()
    }
  }

  fetchData = () => {
    this.setState({ isLoading: true, error: null }, () => {
      const { appName, version } = this.props.match.params
      request(`/${appName}/v2/documentation/${version}`)
        .then(response => {
          this.setState({ versionData: response.data.data })
        })
        .catch(error => {
          this.setState({ error })
        })
        .finally(() => {
          this.setState({ isLoading: false })
        })
    })
  }

  render() {
    const { error, isLoading, versionData } = this.state
    const { appName } = this.props.match.params
    return (
      <ErrorUI error={error} onRetry={this.fetchData}>
        <Loading isLoading={isLoading} size="xxl">
          <StackView axis="vertical" spacing={3}>
            <Details id={versionData.id} {...versionData.attributes} />
            <Changes
              changes={
                dig(versionData, 'relationships', 'changes', 'data') || []
              }
            />
            <Removed
              removed={
                dig(versionData, 'relationships', 'removed', 'data') || []
              }
              appName={appName}
            />
          </StackView>
        </Loading>
      </ErrorUI>
    )
  }
}
